import { useLocale, useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@multaqa/ui/components/ui/dropdown-menu'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { MoreHorizontalIcon, PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { localizedText } from '@/lib/localized-text'
import { type DepartmentResource, useCompanyDepartments } from './api'
import { CompanyDepartmentDialog } from './company-department-dialog'

export function CompanyDepartments({ groupId, companyId }: { groupId: string; companyId: string }) {
  const { t } = useTranslation()
  const departments = useCompanyDepartments(groupId, companyId)
  const messages = useAppErrorMessages()
  const [editing, setEditing] = useState<DepartmentResource | null | 'new'>(null)

  if (departments.isLoading) return <ListSkeleton columns={[2, 1, 1, 1, 0.75, 0.5]} rows={6} />
  if (departments.isError)
    return <AppError error={departments.error} messages={messages} retry={departments.refetch} />

  const list = departments.data ?? []
  return (
    <section data-testid="company-departments" aria-labelledby="company-departments-heading">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 id="company-departments-heading" className="text-h3">
            {t('companies.departments.title')}
          </h2>
          <p className="text-small text-muted-foreground">{t('companies.departments.subtitle')}</p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => setEditing('new')}
          data-testid="company-departments-add"
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
          {t('companies.departments.add')}
        </Button>
      </div>
      {list.length === 0 ? (
        <EmptyState
          title={t('companies.departments.empty')}
          description={t('companies.departments.emptyDescription')}
          action={
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing('new')}>
              {t('companies.departments.add')}
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <Table
            className="min-w-[720px] border border-border"
            data-testid="company-departments-table"
          >
            <TableHeader>
              <TableRow className="bg-muted hover:bg-muted">
                <TableHead>{t('companies.departments.nameEn')}</TableHead>
                <TableHead>{t('companies.departments.nameAr')}</TableHead>
                <TableHead>{t('companies.departments.code')}</TableHead>
                <TableHead>{t('companies.departments.parent')}</TableHead>
                <TableHead>{t('companies.departments.status')}</TableHead>
                <TableHead className="text-end">{t('companies.departments.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((department) => (
                <DepartmentRow
                  key={department.id}
                  department={department}
                  list={list}
                  onEdit={setEditing}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {editing !== null ? (
        <CompanyDepartmentDialog
          key={editing === 'new' ? 'new' : editing.id}
          department={editing === 'new' ? null : editing}
          list={list}
          groupId={groupId}
          companyId={companyId}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        />
      ) : null}
    </section>
  )
}

function DepartmentRow({
  department,
  list,
  onEdit,
}: {
  department: DepartmentResource
  list: DepartmentResource[]
  onEdit: (department: DepartmentResource) => void
}) {
  const { locale } = useLocale()
  const { t } = useTranslation()
  const parent = list.find((item) => item.id === department.parent_id)
  return (
    <TableRow data-testid={`company-department-row-${department.id}`}>
      <TableCell>{localizedText(department.name, 'en')}</TableCell>
      <TableCell dir="rtl">{localizedText(department.name, 'ar')}</TableCell>
      <TableCell className="text-identifier font-mono">{department.code}</TableCell>
      <TableCell>
        {parent ? localizedText(parent.name, locale) : t('companies.common.empty')}
      </TableCell>
      <TableCell>
        <Badge tone={department.is_active ? 'success' : 'neutral'}>
          {t(`companies.departments.${department.is_active ? 'active' : 'inactive'}`)}
        </Badge>
      </TableCell>
      <TableCell className="text-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('companies.departments.rowActions', { code: department.code })}
              data-testid={`company-department-actions-${department.id}`}
            >
              <MoreHorizontalIcon className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onEdit(department)}
              data-testid={`company-department-edit-${department.id}`}
            >
              {t('companies.departments.edit')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}
